/**
 * IN PLAY Block
 * 
 * Raggruppa Qualified, Proposal, Negotiation
 * Ordinata per: rischio, valore, inattività
 */

import { Deal } from '@/hooks/useDeals';
import { Lead } from '@/hooks/useLeads';
import { RevenueFlowDealCard } from './RevenueFlowDealCard';
import { Badge } from '@/components/ui/badge';
import { formatCurrency } from '@/hooks/useUnifiedMetrics';
import { differenceInDays, differenceInHours } from 'date-fns';
import { useMemo } from 'react';

interface InPlayBlockProps {
  deals: Deal[];
  leads: Lead[];
  onCall?: (deal: Deal, lead?: Lead) => void;
  onWhatsApp?: (deal: Deal, lead?: Lead) => void;
  onSendToPhone?: (deal: Deal, lead?: Lead, actionType: 'call' | 'whatsapp') => void;
  onCardClick?: (deal: Deal) => void;
  onMarkWon?: (deal: Deal) => void;
  onMarkLost?: (deal: Deal) => void;
  onReschedule?: (deal: Deal, lead?: Lead) => void;
}

export function InPlayBlock({
  deals,
  leads,
  onCall,
  onWhatsApp,
  onSendToPhone,
  onCardClick,
  onMarkWon,
  onMarkLost,
  onReschedule,
}: InPlayBlockProps) {
  // Filter and sort deals
  const sortedDeals = useMemo(() => {
    const now = new Date();
    
    // Filter: only Qualified, Proposal, Negotiation
    const inPlayDeals = deals.filter(d => {
      const dealStatus = (d as any).deal_status;
      if (dealStatus === 'won' || dealStatus === 'lost' || d.stage === 'won' || d.stage === 'lost') {
        return false;
      }
      return ['qualified', 'proposal_sent', 'negotiation'].includes(d.stage || '');
    });
    
    // Sort by: risk (at risk first), then value (desc), then inactivity
    return inPlayDeals.sort((a, b) => {
      const leadA = leads.find(l => l.id === a.lead_id);
      const leadB = leads.find(l => l.id === b.lead_id);
      
      // Risk score (higher = more risk)
      const getRiskScore = (deal: Deal, lead?: Lead): number => {
        let score = 0;
        const dealValue = deal.value || 0;
        
        // Overdue follow-up
        const nextFollowUp = (lead as any)?.next_follow_up_at ? new Date((lead as any).next_follow_up_at) : null;
        if (nextFollowUp && nextFollowUp < now) {
          const daysOverdue = Math.floor(differenceInDays(now, nextFollowUp));
          score += 1000 + (daysOverdue * 100) + (dealValue * 0.1);
        }
        
        // Never contacted
        const lastContact = lead?.last_interaction_at ? new Date(lead.last_interaction_at) : null;
        if (!lastContact && dealValue > 0) {
          score += 500 + (dealValue * 0.1);
        } else if (lastContact) {
          const hoursSinceContact = differenceInHours(now, lastContact);
          const slaHours = lead?.temperature === 'hot' ? 6 : lead?.temperature === 'warm' ? 24 : 72;
          if (hoursSinceContact > slaHours) {
            const daysSince = Math.floor(hoursSinceContact / 24);
            score += 200 + (daysSince * 50) + (dealValue * 0.05);
          }
        }
        
        // Stalling
        if (deal.updated_at) {
          const daysSinceUpdate = differenceInDays(now, new Date(deal.updated_at));
          if (daysSinceUpdate >= 14) {
            score += 300 + (daysSinceUpdate * 20);
          }
        }
        
        return score;
      };
      
      const riskA = getRiskScore(a, leadA);
      const riskB = getRiskScore(b, leadB);
      
      // First: by risk (desc)
      if (riskA !== riskB) {
        return riskB - riskA;
      }
      
      // Then: by value (desc)
      const valueA = a.value || 0;
      const valueB = b.value || 0;
      if (valueA !== valueB) {
        return valueB - valueA;
      }
      
      // Finally: by inactivity (most inactive first)
      const lastContactA = leadA?.last_interaction_at ? new Date(leadA.last_interaction_at) : null;
      const lastContactB = leadB?.last_interaction_at ? new Date(leadB.last_interaction_at) : null;
      
      if (!lastContactA && lastContactB) return -1;
      if (lastContactA && !lastContactB) return 1;
      if (!lastContactA && !lastContactB) return 0;
      
      const hoursA = differenceInHours(now, lastContactA!);
      const hoursB = differenceInHours(now, lastContactB!);
      return hoursB - hoursA;
    });
  }, [deals, leads]);
  
  const totalValue = sortedDeals.reduce((sum, d) => sum + (d.value || 0), 0);
  const atRiskCount = sortedDeals.filter((deal, idx) => {
    const lead = leads.find(l => l.id === deal.lead_id);
    const now = new Date();
    const nextFollowUp = (lead as any)?.next_follow_up_at ? new Date((lead as any).next_follow_up_at) : null;
    if (nextFollowUp && nextFollowUp < now) return true;
    const lastContact = lead?.last_interaction_at ? new Date(lead.last_interaction_at) : null;
    if (!lastContact) return true;
    const hoursSinceContact = differenceInHours(now, lastContact);
    const slaHours = lead?.temperature === 'hot' ? 6 : lead?.temperature === 'warm' ? 24 : 72;
    return hoursSinceContact > slaHours;
  }).length;
  
  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-2 h-2 rounded-full bg-primary" />
          <h2 className="text-xl font-bold text-foreground">IN PLAY</h2>
          <Badge variant="secondary" className="text-xs font-mono">
            {sortedDeals.length}
          </Badge>
          {atRiskCount > 0 && (
            <Badge variant="destructive" className="text-xs">
              {atRiskCount} at risk
            </Badge>
          )}
        </div>
        <div className="text-sm text-muted-foreground">
          {formatCurrency(totalValue)}
        </div>
      </div>
      
      {/* Deals grid */}
      {sortedDeals.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground text-sm">
          No deals in play
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {sortedDeals.map((deal) => {
            const lead = leads.find(l => l.id === deal.lead_id);
            return (
              <RevenueFlowDealCard
                key={deal.id}
                deal={deal}
                lead={lead}
                onCall={onCall}
                onWhatsApp={onWhatsApp}
                onSendToPhone={onSendToPhone}
                onCardClick={onCardClick}
                onMarkWon={onMarkWon}
                onMarkLost={onMarkLost}
                onReschedule={onReschedule}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

